import {addUrlToHistoryStack} from '@github-ui/history'
import type {IssueReference} from '@github-ui/issue-viewer/Types'
import {ACCOUNT_ISSUE_PATH_PATTERN} from '@github-ui/issue-viewer/Urls'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

type SidePanelIssueBase = {
  number: number
  globalId?: string
}

/** Enough to identify the issue; callers add whatever else their own identifier type carries. */
export type SidePanelIssue = IssueReference & {globalId?: string}

type RepositoryIssueReference = SidePanelIssueBase & {
  owner: string
  repo: string
  isAccountIssue?: boolean
  permalink?: string
}

export function toSidePanelIssue({isAccountIssue, ...issue}: RepositoryIssueReference): SidePanelIssue {
  if (isAccountIssue && issue.permalink) return {...issue, isAccountIssue: true, permalink: issue.permalink}
  return issue
}

/**
 * The side panel's URL contract: `?issue=owner|repo|number` for repository issues and
 * `?issue=orgs|owner|issues|number` (or the equivalent user reference) for account issues.
 */
export const SIDE_PANEL_SEARCH_PARAM = 'issue'

// Dot-only segments are path traversal once interpolated: `/o/../issues/1` is `/issues/1`.
const NAME_SEGMENT = /^(?!\.+$)[\w.-]+$/
const ACCOUNT_ISSUE_REFERENCE = /^(orgs|users)\|([^|]+)\|issues\|(\d+)$/
const REPOSITORY_ISSUE_REFERENCE = /^([^|]+)\|([^|]+)\|(\d+)$/

function accountIssue(scope: string, account: string, rawNumber: string): SidePanelIssue | null {
  const number = Number(rawNumber)
  if (
    (scope !== 'orgs' && scope !== 'users') ||
    !NAME_SEGMENT.test(account) ||
    !Number.isSafeInteger(number) ||
    number < 1
  ) {
    return null
  }

  return {
    number,
    isAccountIssue: true,
    permalink: `/${scope}/${account}/issues/${number}`,
  }
}

export function normalizeSidePanelIssuePermalink(permalink: string): string | null {
  try {
    const url = new URL(permalink, ssrSafeLocation.origin)
    const pathname = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname
    return `${pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

export function parseSidePanelIssue(href: string = ssrSafeLocation.href): SidePanelIssue | null {
  const url = new URL(href, ssrSafeLocation.origin)
  const reference = url.searchParams.get(SIDE_PANEL_SEARCH_PARAM)
  if (!reference) return null

  const accountReferenceMatch = ACCOUNT_ISSUE_REFERENCE.exec(reference)
  if (accountReferenceMatch) {
    const [, scope, account, rawNumber] = accountReferenceMatch
    if (!scope || !account || !rawNumber) return null
    return accountIssue(scope, account, rawNumber)
  }

  // Continue accepting previously shared account issue links that contain the full permalink.
  if (!reference.includes('|')) {
    try {
      const parsedPermalink = new URL(reference, url.origin)
      if (parsedPermalink.origin !== url.origin) return null

      const match = ACCOUNT_ISSUE_PATH_PATTERN.exec(parsedPermalink.pathname)
      const [, scope, account, rawNumber] = match ?? []
      if (!scope || !account || !rawNumber || !accountIssue(scope, account, rawNumber)) return null
      const permalink = normalizeSidePanelIssuePermalink(parsedPermalink.toString())
      if (!permalink) return null

      return {
        number: Number(rawNumber),
        isAccountIssue: true,
        permalink,
      }
    } catch {
      return null
    }
  }

  const repositoryReferenceMatch = REPOSITORY_ISSUE_REFERENCE.exec(reference)
  const [, owner, repo, rawNumber] = repositoryReferenceMatch ?? []
  // Interpolated into hrefs, so `//evil.com` as an owner would resolve off-site.
  if (!owner || !repo || !NAME_SEGMENT.test(owner) || !NAME_SEGMENT.test(repo)) return null

  const number = Number(rawNumber)
  // Rejects `0`, negatives and `12abc`, all of which `parseInt` would have accepted in some form.
  if (!Number.isSafeInteger(number) || number < 1) return null

  return {owner, repo, number}
}

export function serializeSidePanelIssue(issue: SidePanelIssue): string | null {
  if (issue.isAccountIssue && issue.permalink) {
    const permalink = normalizeSidePanelIssuePermalink(issue.permalink)
    if (!permalink) return null
    const match = ACCOUNT_ISSUE_PATH_PATTERN.exec(new URL(permalink, ssrSafeLocation.origin).pathname)
    const [, scope, account, rawNumber] = match ?? []
    if (!scope || !account || !rawNumber || !accountIssue(scope, account, rawNumber)) return null
    return `${scope}|${account}|issues|${rawNumber}`
  }

  return issue.owner && issue.repo ? `${issue.owner}|${issue.repo}|${issue.number}` : null
}

/** `pushState`, not a router navigation: the panel is not a route, and navigating would refetch. */
export function writeSidePanelIssueToUrl(issue: SidePanelIssue | null): void {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  const reference = issue ? serializeSidePanelIssue(issue) : null
  if (reference) {
    url.searchParams.set(SIDE_PANEL_SEARCH_PARAM, reference)
  } else {
    url.searchParams.delete(SIDE_PANEL_SEARCH_PARAM)
  }
  addUrlToHistoryStack(url.toString())
}
